import { CONFIG, LICENSES } from "./config";
import type { Answers, Step, Variant } from "./types";

/** 選択肢の画像。public/img 配下に webp を置いています */
const IMG = {
  imasugu: "/img/imasugu.webp",
  kensaku: "/img/kensaku.webp",
  male: "/img/male.webp",
  female: "/img/female.webp",
  hige: "/img/hige.webp",
  chapatsu: "/img/chapatsu.webp",
  tattoo: "/img/tattoo.webp",
  smoke: "/img/smoke.webp",
  jiko: "/img/jiko.webp",
  zenka: "/img/zenka.webp",
  jibyo: "/img/jibyo.webp",
  ryo: "/img/ryo.webp",
} as const;

/** 選んだ免許にAT限定の区分が存在するときだけ AT の設問を出します */
const atPossible = (a: Answers) => LICENSES.some((l) => l.v === a.license && l.at);

/** 郵便番号が未入力（7桁になっていない）ときだけ希望エリアを聞きます */
const zipEmpty = (a: Answers) => !/^\d{7}$/.test(a.zip.replace(/[^\d]/g, ""));

/** B（/drivers）だけに出る追加設問。ここが A/B の唯一の差分です */
const PREFER_STEP: Step = {
  id: "prefer",
  type: "checkImg",
  key: "prefer",
  title: "こだわりの条件など",
  help: "受け入れ可能な会社の求人をお探しします。あてはまるものをすべて選んでください。",
  options: [
    { v: "ひげOK", img: IMG.hige },
    { v: "茶髪OK", img: IMG.chapatsu },
    { v: "刺青・タトゥーOK", img: IMG.tattoo },
    { v: "車内喫煙OK", img: IMG.smoke },
    { v: "事故歴不問", img: IMG.jiko },
    { v: "前科不問", img: IMG.zenka },
    { v: "持病持ちOK", img: IMG.jibyo },
    { v: "寮・社宅あり", img: IMG.ryo },
  ],
  none: "特になし",
};

export function buildSteps(variant: Variant): Step[] {
  const steps: Step[] = [
    {
      id: "intent",
      type: "tiles",
      key: "intent",
      required: true,
      title: "お気持ちはどちらに近いですか？",
      options: [
        { v: "すぐに働きたい", img: IMG.imasugu },
        { v: "いまは情報収集したい", img: IMG.kensaku },
      ],
    },
    {
      id: "license",
      type: "radio",
      key: "license",
      required: true,
      title: "お持ちの免許はどれですか？",
      help: "一番上位の免許をひとつ選んでください。",
      options: LICENSES.map((l) => l.v),
    },
    {
      id: "at",
      type: "radio",
      key: "at",
      when: atPossible,
      title: "AT限定免許ですか？",
      options: ["はい", "いいえ"],
    },
    {
      id: "quals",
      type: "check",
      key: "quals",
      title: "その他にお持ちの免許や資格はありますか？",
      help: "あてはまるものをすべて選んでください。",
      options: [
        "フォークリフト", "けん引免許", "玉掛け", "小型移動式クレーン",
        "危険物取扱者", "運行管理者", "普通二種（タクシー）", "大型二種（バス）",
      ],
      none: "なし",
    },
    {
      id: "status",
      type: "radio",
      key: "status",
      title: "お仕事の状況",
      options: ["離職中", "就業中"],
    },
    ...(variant === "B" ? [PREFER_STEP] : []),
    {
      id: "zip",
      type: "tel",
      key: "zip",
      title: "お住まいの郵便番号",
      help: "ハイフンなし7桁。入力いただくとお近くの求人からご紹介します。",
      placeholder: "4640850",
      maxlength: 8,
      mode: "numeric",
      zip: true,
      validate: (v) =>
        !v || /^\d{7}$/.test(v.replace(/[^\d]/g, "")) ? "" : "7桁の数字で入力してください。",
    },
    {
      id: "area",
      type: "text",
      key: "area",
      when: zipEmpty,
      title: "ご希望の就業エリア",
      help: "郵便番号の代わりに、通える範囲をおしえてください。",
      placeholder: CONFIG.areaPlaceholder,
    },
    {
      id: "gender",
      type: "tiles",
      key: "gender",
      title: "性別",
      options: [
        { v: "男性", img: IMG.male },
        { v: "女性", img: IMG.female },
      ],
    },
    {
      id: "name",
      type: "name",
      required: true,
      title: "お名前",
      help: "担当者からのご連絡に使用します。",
    },
    {
      id: "birth",
      type: "birth",
      required: true,
      title: "生年月日（西暦）",
      // A のみ補足を出す（元の driver-form_1.html の文言を維持）
      help: variant === "A" ? "人材紹介会社の求職管理簿の記録に使用します。" : undefined,
      validate: (v) => {
        if (!v) return "";
        const [y, m, d] = v.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
          return "存在しない日付です。";
        }
        return "";
      },
    },
    {
      id: "tel",
      type: "tel",
      key: "tel",
      required: true,
      title: "携帯電話",
      help: "求人のご案内はお電話・SMSでお送りします。",
      placeholder: "09012345678",
      maxlength: 13,
      mode: "tel",
      validate: (v) =>
        /^0\d{9,10}$/.test(v.replace(/[^\d]/g, ""))
          ? ""
          : "090から始まる番号など、有効な電話番号を入力してください。",
    },
    { id: "consent", type: "consent", required: true, title: "利用規約・個人情報の取扱い" },
  ];
  return steps;
}
